import type { SsoProviderId } from "@/shared/login-methods.ts";
import type { OauthState } from "@/types/oauth.ts";

export const parseOauthState = (rawState: unknown): OauthState | null => {
	try {
		if (typeof rawState === "string") {
			const decoded = decodeURIComponent(rawState);
			return JSON.parse(decoded) as OauthState;
		}
		if (typeof rawState === "object" && rawState !== null) {
			return rawState as OauthState;
		}
		return null;
	} catch {
		return null;
	}
};

/**
 * Decode the payload segment of a signed OAuth `state` —
 * `<base64url-payload>.<base64url-hmac>`, minted by `api/util/oauth-state.ts`.
 * Signed states are opaque to `parseOauthState` (they are not JSON `OauthState`),
 * so the route needs this to tell *which* flow a redirect belongs to.
 *
 * FE ROUTING ONLY. The HMAC is not checked here and cannot be — the key is
 * server-side. Every consuming endpoint re-verifies the signature, so a forged
 * payload gets rejected there; nothing read out of this function ever confers
 * trust or gates access.
 */
const peekSignedOAuthState = (
	rawState: unknown,
): Record<string, unknown> | null => {
	if (typeof rawState !== "string") {
		return null;
	}
	const dot = rawState.lastIndexOf(".");
	if (dot <= 0) {
		return null;
	}
	try {
		const b64 = rawState.slice(0, dot).replace(/-/g, "+").replace(/_/g, "/");
		const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, "=");
		const payload: unknown = JSON.parse(atob(padded));
		return typeof payload === "object" && payload !== null
			? (payload as Record<string, unknown>)
			: null;
	} catch {
		return null;
	}
};

/** The SSO login flow's signed state, as far as FE routing needs to read it. */
export interface SsoOauthStatePeek {
	provider: SsoProviderId;
	redirect?: string;
}

/**
 * Recognize the pre-login SSO `state` by its payload shape (`secretHash` + a
 * known `provider`) and return the fields the SSO surface needs for routing:
 * which provider to complete against, and where to land afterwards.
 *
 * The `redirect` returned here is only a navigation hint. It was validated as an
 * in-app path when the state was minted and is covered by the HMAC, so it cannot
 * have been swapped en route.
 */
export const peekSsoOauthState = (
	rawState: unknown,
): SsoOauthStatePeek | null => {
	const payload = peekSignedOAuthState(rawState);
	if (typeof payload?.secretHash !== "string") {
		return null;
	}
	if (
		payload.provider !== "microsoft" &&
		payload.provider !== "google" &&
		payload.provider !== "apple"
	) {
		return null;
	}
	return {
		provider: payload.provider,
		redirect:
			typeof payload.redirect === "string" ? payload.redirect : undefined,
	};
};

/**
 * Extract the frontend routing payload from a connector-connect `state` (minted by
 * `/api/spaces/link`). The backend wraps whatever the wizard sent inside the signed
 * envelope, so this unwraps it back into the `OauthState` the route already knows
 * how to act on.
 *
 * Discriminated by shape. FE ROUTING ONLY —
 * every consumer re-verifies the HMAC, and the `payload` is never a trust input
 * server-side either.
 */
export const peekSpaceConnectPayload = (
	rawState: unknown,
): OauthState | null => {
	const decoded = peekSignedOAuthState(rawState);
	if (
		typeof decoded?.userId !== "string" ||
		typeof decoded.orgId !== "string" ||
		typeof decoded.provider !== "string"
	) {
		return null;
	}
	const payload = decoded.payload;
	return typeof payload === "object" && payload !== null
		? (payload as OauthState)
		: null;
};

/**
 * Where a connector-connect return has to LAND, keyed on the `returnUrl` the starting
 * page put in its state — or `undefined` when no page can complete it.
 *
 * ## Why this is one function and not two lists
 *
 * `/auth/oauth` asks the same question twice: once to decide whether to redirect at all
 * (`shouldRedirectForStorage`) and once to decide where to (`getStorageRedirectTarget`).
 * Those were two separate `returnUrl.includes(...)` chains, and a page added to the second
 * without the first is unreachable — the redirect never fires, so the branch that would
 * have routed it is dead code and the consent `code` is dropped on the floor with no error.
 *
 * That is exactly what happened when HubSpot's connect act started returning to
 * the integrations page: the target branch was added, the gate was not, and
 * `shouldRedirectForStorage` fell through to `formData !== undefined` — false for a
 * connector state, which carries only a `returnUrl`. The Spaces wizard was unaffected
 * only because its `returnUrl` is `window.location.pathname`, which contains `/spaces`.
 *
 * So both callers read this instead (Rule #0). Adding a page is one row here, and the
 * gate and the target cannot disagree about it.
 *
 * Matched on `includes` rather than equality because the Spaces flow returns the live
 * pathname, which carries a space id (`/dashboard/spaces/<uuid>`). `/integrations` also
 * catches the page's former home at `/dashboard/integrations`, so a consent started
 * before the move still completes. The two patterns do not overlap, so order is not
 * load-bearing.
 */
const CONNECT_RETURN_TARGETS = [
	{ match: "/spaces", to: "/dashboard/spaces" },
	{ match: "/integrations", to: "/settings/integrations" },
] as const;

export type ConnectReturnTarget = (typeof CONNECT_RETURN_TARGETS)[number]["to"];

export const connectReturnTarget = (
	returnUrl: unknown,
): ConnectReturnTarget | undefined =>
	typeof returnUrl === "string"
		? CONNECT_RETURN_TARGETS.find((target) => returnUrl.includes(target.match))
				?.to
		: undefined;

/**
 * The PROVIDER a connector-connect `state` belongs to, or null when the state is not
 * one.
 *
 * `/settings/integrations` hosts several connector connect flows, and they all see the
 * same `?code&state` on return. Without the provider, whichever section renders first
 * consumes the code and spends it against the wrong flow, losing the user's consent.
 *
 * Reads the OUTER envelope's `provider` — the field `/api/spaces/link` signs — where
 * {@link peekSpaceConnectPayload} returns the frontend routing payload nested inside it.
 *
 * FE ROUTING ONLY, like its siblings: the HMAC cannot be checked here and every
 * consuming endpoint re-verifies it, so nothing read out of this confers trust.
 */
export const spaceConnectStateProvider = (rawState: unknown): string | null => {
	const decoded = peekSignedOAuthState(rawState);
	if (
		typeof decoded?.userId !== "string" ||
		typeof decoded.orgId !== "string" ||
		typeof decoded.provider !== "string"
	) {
		return null;
	}
	return decoded.provider;
};

/**
 * Where every MCP server authorization returns: the user's integrations page,
 * which completes it. Whoever authorizes a server is an admin of it, so it is
 * on their own list there — wherever they started.
 */
export const MCP_AUTHORIZATION_RETURN_URL = "/settings/integrations";

/**
 * The routing data an MCP server authorization carries through the consent
 * (`startMcpServerAuthorization`). `returnUrl` is what `/auth/oauth` routes on;
 * the rest says which server to complete and which page to go back to.
 */
export interface McpAuthorizationRouting {
	returnUrl: typeof MCP_AUTHORIZATION_RETURN_URL;
	mcpServerId: string;
	backTo: string;
}

/**
 * The MCP server authorization a returned `state` belongs to, or null when it is
 * not one. Recognized by the provider the backend signed (`mcp`) and the routing
 * fields inside it.
 *
 * FE ROUTING ONLY, like its siblings: the completing endpoint re-verifies the
 * HMAC and binds the code to the server through PKCE.
 */
export const peekMcpAuthorizationReturn = (
	rawState: unknown,
): McpAuthorizationRouting | null => {
	if (spaceConnectStateProvider(rawState) !== "mcp") {
		return null;
	}
	const decoded = peekSignedOAuthState(rawState);
	const payload = decoded?.payload;
	if (
		typeof payload !== "object" ||
		payload === null ||
		!("mcpServerId" in payload) ||
		!("backTo" in payload) ||
		typeof payload.mcpServerId !== "string" ||
		typeof payload.backTo !== "string"
	) {
		return null;
	}
	return {
		returnUrl: MCP_AUTHORIZATION_RETURN_URL,
		mcpServerId: payload.mcpServerId,
		backTo: payload.backTo,
	};
};

export const serializeOauthState = (state: unknown): string | null => {
	if (state === undefined) {
		return null;
	}
	if (typeof state === "string") {
		return state;
	}
	try {
		return JSON.stringify(state);
	} catch {
		return null;
	}
};
